/**
 * URL Unlocking API Functions
 */

import constants from '../../../constants';
import {
  ApiResponse,
  PagedList,
  PageUrlElementModel,
  PublicPageData,
  ExtractedEntityIds,
  UrlTargetType
} from './types';
import { ENV_SUBDOMAINS } from './constants';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

// Session helpers
const getSession = () => {
  const localStorageSession = localStorage['session'];
  return localStorageSession && localStorageSession !== 'undefined'
    ? JSON.parse(localStorageSession)
    : null;
};

/**
 * Headers for public API calls (API key only)
 */
const getPublicHeaders = (): Record<string, string> => ({
  'ApiKey': API_KEY || '',
  'Content-Type': 'application/json'
});

/**
 * Headers for authenticated API calls
 */
const getAuthHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    'ApiKey': API_KEY || '',
    'Content-Type': 'application/json',
    'Authorization': session?.key ? `bearer ${session.key}` : ''
  };
};

/**
 * Handle API response
 */
const handleResponse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${errorText}`);
  }
  return res.json();
};

/**
 * PUBLIC GET - Fetch public page data to extract entity IDs
 * Endpoint: /api/v2/page/public/{primary}/{secondary}/{tertiary}
 */
export const fetchPublicPageData = async (
  primary: string,
  secondary?: string,
  tertiary?: string
): Promise<ApiResponse<PublicPageData>> => {
  // Build path segments, filtering out empty ones
  const pathSegments = [primary, secondary, tertiary].filter(Boolean);
  const path = pathSegments.join('/');
  
  const url = `${API_BASE}/api/v2/page/public/${path}`;
  
  const res = await fetch(url, {
    method: 'GET',
    headers: getPublicHeaders()
  });
  
  return handleResponse<ApiResponse<PublicPageData>>(res);
};

/**
 * Extract entity IDs from public page data
 */
export const extractEntityIds = (data: PublicPageData): ExtractedEntityIds => ({
  organizationId: data.organization?.id,
  eventId: data.event?.id,
  campaignId: data.campaign?.id,
  fundraiserId: data.fundraiser?.id,
  collectionId: data.collection?.id,
  seriesId: data.series?.id
});

/**
 * Build URL scope key based on target type and entity IDs
 */
export const buildUrlScopeKey = (
  targetType: UrlTargetType,
  urlPath: string,
  ids: ExtractedEntityIds
): string | null => {
  switch (targetType) {
    case 'primary':
      return `${urlPath}-B`;
    
    case 'secondary':
      if (ids.eventId && ids.organizationId) {
        return `${urlPath}-E-${ids.eventId}-O-${ids.organizationId}`;
      }
      if (ids.organizationId) {
        return `${urlPath}-C-${ids.organizationId}`;
      }
      return null; // Missing required IDs
    
    case 'tertiary':
      if (ids.campaignId) {
        return `${urlPath}-P-${ids.campaignId}`;
      }
      return null; // Missing required campaign ID
    
    default:
      return null;
  }
};

/**
 * GET PageUrl - Fetch page URL records by scope key
 * Endpoint: /api/v2/pageUrl/{urlScope}
 */
export const fetchPageUrlByScope = async (
  urlScope: string
): Promise<ApiResponse<PagedList<PageUrlElementModel>>> => {
  const url = `${API_BASE}/api/v2/pageUrl/${encodeURIComponent(urlScope)}`;
  
  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse<ApiResponse<PagedList<PageUrlElementModel>>>(res);
};

/**
 * DELETE PageUrl - Delete a page URL record
 * Endpoint: /api/v2/pageUrl/{id}
 * Returns 204 No Content on success
 */
export const deletePageUrl = async (id: number): Promise<{ success: boolean; error?: string }> => {
  const url = `${API_BASE}/api/v2/pageUrl/${id}`;
  
  const res = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  // 204 No Content is the expected success response
  if (res.status === 204 || res.ok) {
    return { success: true };
  }
  
  // Handle error responses
  const errorText = await res.text().catch(() => res.statusText);
  throw new Error(`API ${res.status}: ${errorText}`);
};

/**
 * Parse a full URL into path components
 * Handles production and non-production environments:
 * - Production: https://primary.grassrootz.com/secondary/tertiary
 * - Dev: https://primary.dev.grassrootz.com/secondary/tertiary
 * - Test: https://primary.test.grassrootz.com/secondary/tertiary
 */
export const parseFullUrl = (fullUrl: string): {
  isValid: boolean;
  isValidDomain: boolean;
  primary: string;
  secondary: string;
  tertiary: string;
  error?: string;
} => {
  const result = {
    isValid: false,
    isValidDomain: false,
    primary: '',
    secondary: '',
    tertiary: ''
  };

  try {
    const url = new URL(fullUrl.trim());
    
    const hostParts = url.hostname.toLowerCase().split('.');
    
    // Check for valid grassrootz.com domain patterns:
    // - grassrootz.com (at least 2 parts)
    // - *.grassrootz.com (production with subdomain)
    // - *.dev.grassrootz.com (dev environment)
    // - *.test.grassrootz.com (test environment)
    const lastTwo = hostParts.slice(-2).join('.');
    
    if (lastTwo !== 'grassrootz.com') {
      return { ...result, isValid: true, error: 'invalidDomain' };
    }
    
    result.isValidDomain = true;
    result.isValid = true;
    
    // Extract primary (subdomain), filtering out environment subdomains
    // Examples:
    // - charity.grassrootz.com → primary = "charity"
    // - vault-tec.dev.grassrootz.com → primary = "vault-tec"
    // - org.test.grassrootz.com → primary = "org"
    if (hostParts.length >= 3) {
      // Get all parts before grassrootz.com
      const subdomainParts = hostParts.slice(0, -2);
      // Filter out environment subdomains (dev, test, staging, uat)
      const primaryParts = subdomainParts.filter(part => !ENV_SUBDOMAINS.includes(part));
      result.primary = primaryParts.join('.');
    }
    
    // Extract path segments
    const pathSegments = url.pathname.split('/').filter(Boolean);
    result.secondary = pathSegments[0] || '';
    result.tertiary = pathSegments[1] || '';
    
    return result;
  } catch {
    return { ...result, error: 'invalidUrl' };
  }
};

/**
 * Validate form inputs based on target type
 */
export const validateInputs = (
  targetType: UrlTargetType,
  primary: string,
  secondary: string,
  tertiary: string
): { isValid: boolean; error?: string } => {
  if (!primary.trim()) {
    return { isValid: false, error: 'missingPrimary' };
  }
  
  if (targetType === 'secondary' && !secondary.trim()) {
    return { isValid: false, error: 'missingSecondary' };
  }
  
  if (targetType === 'tertiary') {
    if (!secondary.trim()) {
      return { isValid: false, error: 'missingSecondary' };
    }
    if (!tertiary.trim()) {
      return { isValid: false, error: 'missingTertiary' };
    }
  }
  
  return { isValid: true };
};
